import { gql, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { ConditionInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { BlockedEmailEntity } from './blocked-email.types';

export const DELETE_BLOCKED_EMAIL_QUERY = gql`
  mutation DeleteBlockedEmail($conditions: [ConditionInput]) {
    deleteBlockedEmail(conditions: $conditions) {
      id
    }
  }
`;

export interface DeleteBlockedEmailMutation {
  deleteBlockedEmail: Pick<BlockedEmailEntity, 'id'>[];
}

export type DeleteBlockedEmailMutationParams = {
  conditions: ConditionInput[];
};

export const useDeleteBlockedEmail = () => {
  const { showErrorAlert } = useNotifications();
  const [deleteBlockedEmailMutation, { loading: isDeleting }] = useMutation<
    DeleteBlockedEmailMutation,
    DeleteBlockedEmailMutationParams
  >(DELETE_BLOCKED_EMAIL_QUERY, {
    update(cache, { data }) {
      if (!data?.deleteBlockedEmail?.length) {
        return;
      }
      data.deleteBlockedEmail.forEach((email) => {
        const id = cache.identify({
          __typename: 'BlockedEmail',
          id: email.id,
        });
        cache.evict({ id });
      });
      cache.gc();
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to deleting blocked email, please try again');
    },
  });

  const deleteBlockedEmailsById = useCallback(
    async (blockedEmailIds: string[]) => {
      await deleteBlockedEmailMutation({
        variables: {
          conditions: [
            {
              attribute: 'blockedEmailId',
              operator: 'in',
              value: blockedEmailIds.join(','),
            },
          ],
        },
        optimisticResponse: {
          deleteBlockedEmail: blockedEmailIds.map((id) => ({
            __typename: 'BlockedEmail',
            id,
          })),
        },
      });
    },
    [deleteBlockedEmailMutation],
  );

  return {
    deleteBlockedEmailsById,
    isDeleting,
  };
};
